// Main API exports for pages
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Browser: same-origin /api (CRA proxy or nginx) — avoids CORS.
 * SSR/build scripts: full REACT_APP_API_URL.
 */
export function apiPath(path: string): string {
  const segment = path.startsWith('/') ? path : `/${path}`;
  const normalized = segment.startsWith('/api') ? segment : `/api${segment}`;
  if (typeof window !== 'undefined') {
    return normalized;
  }
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');
  const suffix = normalized.replace(/^\/api/, '');
  return `${base}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
}

// Fetch with timeout helper
const fetchWithTimeout = async (url: string, options: any = {}, timeout: number = 120000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, { 
      ...options, 
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout (${timeout}ms)`);
    }
    throw error;
  }
};

// Get product by slug
export const getProductBySlug = async (slug: string) => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/products/slug/${slug}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      throw new Error(`API Error: ${res.status} ${res.statusText}`);
    }
    const data = await res.json();
    return data;
  } catch (error: any) {
    console.error('getProductBySlug error:', error);
    throw error;
  }
};

// Get banners
export const getBanners = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/banners`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getBanners error:', error);
    throw error;
  }
};

// Get deals
export const getDeals = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/deals`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getDeals error:', error);
    throw error;
  }
};

// Get categories
export const getCategories = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/categories`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getCategories error:', error);
    throw error;
  }
};

// Helper to get auth headers (customer token → price level on product APIs)
export const getAuthHeaders = (): Record<string, string> => {
  // Only use user token for user-facing APIs
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get products
export const getProducts = async (params?: any) => {
  try {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const res = await fetchWithTimeout(`${API_URL}/products${queryString}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getProducts error:', error);
    throw error;
  }
};

// Get featured products
export const getFeaturedProducts = async (limit = 8) => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/products/featured/list?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getFeaturedProducts error:', error);
    throw error;
  }
};

// Search products
export const searchProducts = async (query: string) => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/products/search?q=${encodeURIComponent(query)}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('searchProducts error:', error);
    throw error;
  }
};

/** Homepage CMS sections with customer-specific product prices when logged in */
export const getHomepageSections = async () => {
  try {
    const res = await fetchWithTimeout(apiPath('/homepage-sections/public'), {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getHomepageSections error:', error);
    throw error;
  }
};

// Get store settings
export const getStoreSettings = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/admin/ultra-settings/public/store`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getStoreSettings error:', error);
    throw error;
  }
};

// Get footer content
export const getFooterContent = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/footer/content`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getFooterContent error:', error);
    throw error;
  }
};

// Get navigation/header
export const getHeaderNavigation = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/navigation/header`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getHeaderNavigation error:', error);
    throw error;
  }
};

// Get brands
export const getBrands = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/brands/public`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getBrands error:', error);
    throw error;
  }
};

export const getBrandBySlug = async (slug: string) => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/brands/public/${encodeURIComponent(slug)}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getBrandBySlug error:', error);
    throw error;
  }
};

// Get active coupons
export const getActiveCoupons = async () => {
  try {
    const res = await fetchWithTimeout(`${API_URL}/coupons/active`);
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  } catch (error: any) {
    console.error('getActiveCoupons error:', error);
    throw error;
  }
};

// Currency config
export const CURRENCY_CONFIG = {
  code: 'INR',
  symbol: '₹',
  name: 'Indian Rupee',
  locale: 'en-IN',
  decimalDigits: 0
};

// Image base URL
export const IMAGE_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

// Get category storefront (sections + productCount) by slug
export const getProductsByCategory = async (categorySlug: string, params?: Record<string, string | number | undefined>) => {
  try {
    const clean: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') clean[k] = String(v);
      });
    }
    const queryString = Object.keys(clean).length ? '?' + new URLSearchParams(clean).toString() : '';
    const res = await fetchWithTimeout(
      `${API_URL}/categories/slug/${encodeURIComponent(categorySlug)}${queryString}`,
      { headers: getAuthHeaders() }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || `API Error: ${res.status}`);
    }
    return data;
  } catch (error: any) {
    console.error('getProductsByCategory error:', error);
    throw error;
  }
};
